<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'

const props = defineProps({
  workspaces: {
    type: Array,
    default: () => [],
  },
  current_slug: {
    type: String,
    default: null,
  },
})

const isOpen = ref(false)
const root = ref(null)
const trigger = ref(null)
const itemRefs = ref([])
const activeIndex = ref(-1)
const dropdownId = `workspace-switcher-${Math.random().toString(36).slice(2)}`

const workspaceOptions = computed(() => (Array.isArray(props.workspaces) ? props.workspaces : []))
const selectedIndex = computed(() => workspaceOptions.value.findIndex((workspace) => workspace.slug === props.current_slug))
const currentWorkspace = computed(() => workspaceOptions.value[selectedIndex.value])
const triggerLabel = computed(() => currentWorkspace.value?.name || 'Select workspace')

const setItemRef = (el, index) => {
  if (el) {
    itemRefs.value[index] = el
  }
}

const focusItem = (index) => {
  const count = workspaceOptions.value.length

  if (count === 0) {
    activeIndex.value = -1
    return
  }

  const nextIndex = (index + count) % count
  activeIndex.value = nextIndex
  nextTick(() => itemRefs.value[nextIndex]?.focus())
}

const openDropdown = (focusIndex = selectedIndex.value >= 0 ? selectedIndex.value : 0) => {
  isOpen.value = true
  focusItem(focusIndex)
}

const closeDropdown = (returnFocus = false) => {
  isOpen.value = false
  activeIndex.value = -1

  if (returnFocus) {
    nextTick(() => trigger.value?.focus())
  }
}

const toggleDropdown = () => {
  if (isOpen.value) {
    closeDropdown()
    return
  }

  openDropdown()
}

const handleDocumentClick = (event) => {
  if (!isOpen.value || root.value?.contains(event.target)) {
    return
  }

  closeDropdown()
}

const handleTriggerKeydown = (event) => {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    openDropdown(selectedIndex.value >= 0 ? selectedIndex.value : 0)
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    openDropdown(workspaceOptions.value.length - 1)
  } else if (event.key === 'Escape') {
    closeDropdown(true)
  }
}

const handleItemKeydown = (event, index) => {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    focusItem(index + 1)
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    focusItem(index - 1)
  } else if (event.key === 'Home') {
    event.preventDefault()
    focusItem(0)
  } else if (event.key === 'End') {
    event.preventDefault()
    focusItem(workspaceOptions.value.length - 1)
  } else if (event.key === 'Escape') {
    event.preventDefault()
    closeDropdown(true)
  } else if (event.key === ' ') {
    event.preventDefault()
    event.currentTarget.click()
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<template>
  <div ref="root" class="relative">
    <button
      ref="trigger"
      type="button"
      class="flex w-full items-center justify-between gap-3 border border-slate-200 bg-white px-3 py-2 text-left text-sm font-semibold text-slate-950 shadow-sm hover:bg-slate-50 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 dark:focus:border-indigo-400 dark:focus:ring-indigo-500/20"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="dropdownId"
      @click="toggleDropdown"
      @keydown="handleTriggerKeydown"
    >
      <span class="truncate">{{ triggerLabel }}</span>
      <span aria-hidden="true" class="h-2 w-2 shrink-0 rotate-45 border-b border-r border-slate-400 dark:border-slate-500"></span>
    </button>

    <div
      v-if="isOpen"
      :id="dropdownId"
      role="listbox"
      class="absolute left-0 right-0 z-20 mt-2 max-h-72 overflow-y-auto border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:shadow-black/40"
    >
      <a
        v-for="(workspace, index) in workspaceOptions"
        :key="workspace.slug"
        :ref="(el) => setItemRef(el, index)"
        :href="workspace.url"
        role="option"
        tabindex="-1"
        :aria-selected="workspace.slug === current_slug"
        class="block px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-500/20"
        :class="[
          workspace.slug === current_slug
            ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300'
            : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-100',
          activeIndex === index ? 'ring-2 ring-indigo-100 dark:ring-indigo-500/20' : '',
        ]"
        @focus="activeIndex = index"
        @mouseenter="activeIndex = index"
        @click="closeDropdown(false)"
        @keydown="handleItemKeydown($event, index)"
      >
        <span class="block truncate">{{ workspace.name }}</span>
      </a>

      <p v-if="workspaceOptions.length === 0" class="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">No workspaces yet.</p>
    </div>
  </div>
</template>
